/**
 * The HUD's one button and its tooltips (spec v2 §5, §6). A button shows an icon only; its
 * name, shortcut and, when it can't act, the reason live in its tooltip and accessible name.
 * Unavailable buttons stay focusable and explain themselves, but never act (aria-disabled).
 */
import { useEffect, useId, useState } from 'react';
import * as css from './Hud.css.ts';
import { Icon, type IconName } from './icons.tsx';

export interface HudButtonProps {
  readonly icon: IconName;
  readonly label: string;
  readonly keyHint?: string;
  /** Why it can't act now; shown in the tooltip and read out. */
  readonly why?: string | undefined;
  /** A second tooltip line that always shows, e.g. "can't be undone". */
  readonly note?: string | undefined;
  readonly disabled?: boolean;
  readonly pressed?: boolean;
  readonly expanded?: boolean;
  /** `accent`: the active, primary look; `danger`: an attack; `float`: alone over the map. */
  readonly tone?: 'plain' | 'accent' | 'danger' | 'float';
  /** `lg`: the turn's primary action. */
  readonly size?: 'sm' | 'md' | 'lg';
  readonly badge?: number | string | undefined;
  /** False: no tooltip (its name and reason are still read out). */
  readonly tip?: boolean;
  readonly className?: string;
  readonly testId?: string | undefined;
  readonly onClick: () => void;
}

export function HudButton({
  icon,
  label,
  keyHint,
  why,
  note,
  disabled = false,
  pressed,
  expanded,
  tone = 'plain',
  size = 'md',
  badge,
  tip = true,
  className,
  testId,
  onClick,
}: HudButtonProps) {
  const whyId = useId();
  const reason = disabled && why ? why : note;
  return (
    <button
      type="button"
      className={[css.hudButton, className].filter(Boolean).join(' ')}
      data-tone={tone}
      data-size={size}
      data-testid={testId}
      aria-label={label}
      aria-disabled={disabled || undefined}
      aria-pressed={pressed}
      aria-expanded={expanded}
      aria-describedby={reason ? whyId : undefined}
      data-tip={tip ? label : undefined}
      data-key={tip ? keyHint : undefined}
      data-why={tip ? reason : undefined}
      onClick={(e) => {
        if (disabled) {
          e.preventDefault();
          return;
        }
        onClick();
      }}
    >
      <Icon name={icon} />
      {badge !== undefined ? <span className={css.badge}>{badge}</span> : null}
      {reason ? (
        <span id={whyId} className={css.srOnly}>
          {reason}
        </span>
      ) : null}
    </button>
  );
}

/** How long a finger must rest on a control to ask what it is, ms. */
const HOLD_MS = 450;
/** A mouse rests this long before the tooltip shows, ms. */
const HOVER_MS = 280;
/** How long a tooltip asked for by touch stays, ms. */
const TOUCH_SHOW_MS = 2000;
/** A finger that moves this far is scrolling, not holding, CSS px. */
const SLOP_PX = 10;

interface Tip {
  readonly label: string;
  readonly keyHint?: string | undefined;
  readonly why?: string | undefined;
  readonly rect: {
    readonly top: number;
    readonly bottom: number;
    readonly left: number;
    readonly width: number;
  };
}

const tipOf = (el: HTMLElement): Tip => {
  const r = el.getBoundingClientRect();
  return {
    label: el.dataset.tip ?? '',
    keyHint: el.dataset.key,
    why: el.dataset.why,
    rect: { top: r.top, bottom: r.bottom, left: r.left, width: r.width },
  };
};

const focusVisible = (el: Element) => {
  try {
    return el.matches(':focus-visible');
  } catch {
    return false;
  }
};

/**
 * One tooltip for the whole game screen, for every element with `data-tip`: on mouse hover,
 * on keyboard focus, and on a long press by touch (which then swallows the click, so asking
 * what a button does never does it). Mount once.
 */
export function TooltipLayer() {
  const [tip, setTip] = useState<Tip | null>(null);
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    let hide: ReturnType<typeof setTimeout> | undefined;
    let press: { x: number; y: number } | null = null;
    let swallow = false;
    const tipped = (e: Event) =>
      e.target instanceof Element ? e.target.closest<HTMLElement>('[data-tip]') : null;
    const close = () => {
      clearTimeout(timer);
      setTip(null);
    };
    const over = (e: PointerEvent) => {
      if (e.pointerType !== 'mouse') return;
      const el = tipped(e);
      clearTimeout(timer);
      if (!el) {
        setTip(null);
        return;
      }
      timer = setTimeout(() => {
        setTip(tipOf(el));
      }, HOVER_MS);
    };
    const out = (e: PointerEvent) => {
      if (e.pointerType !== 'mouse') return;
      const to = e.relatedTarget instanceof Element ? e.relatedTarget.closest('[data-tip]') : null;
      if (to && to === tipped(e)) return;
      close();
    };
    const down = (e: PointerEvent) => {
      clearTimeout(timer);
      if (e.pointerType === 'mouse') {
        setTip(null);
        return;
      }
      const el = tipped(e);
      if (!el) return;
      press = { x: e.clientX, y: e.clientY };
      timer = setTimeout(() => {
        swallow = true;
        setTip(tipOf(el));
        clearTimeout(hide);
        hide = setTimeout(() => {
          setTip(null);
        }, TOUCH_SHOW_MS);
      }, HOLD_MS);
    };
    const move = (e: PointerEvent) => {
      if (press && Math.hypot(e.clientX - press.x, e.clientY - press.y) > SLOP_PX) {
        clearTimeout(timer);
        press = null;
      }
    };
    const up = () => {
      clearTimeout(timer);
      press = null;
    };
    const click = (e: MouseEvent) => {
      if (!swallow) return;
      swallow = false;
      e.preventDefault();
      e.stopPropagation();
    };
    const menu = (e: Event) => {
      if (tipped(e)) e.preventDefault();
    };
    const focus = (e: FocusEvent) => {
      const el = tipped(e);
      if (el && focusVisible(el)) setTip(tipOf(el));
    };
    const key = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setTip(null);
    };
    document.addEventListener('pointerover', over);
    document.addEventListener('pointerout', out);
    document.addEventListener('pointerdown', down, true);
    document.addEventListener('pointermove', move);
    document.addEventListener('pointerup', up);
    document.addEventListener('pointercancel', up);
    document.addEventListener('click', click, true);
    document.addEventListener('contextmenu', menu);
    document.addEventListener('focusin', focus);
    document.addEventListener('focusout', close);
    document.addEventListener('keydown', key);
    window.addEventListener('scroll', close, true);
    return () => {
      clearTimeout(timer);
      clearTimeout(hide);
      document.removeEventListener('pointerover', over);
      document.removeEventListener('pointerout', out);
      document.removeEventListener('pointerdown', down, true);
      document.removeEventListener('pointermove', move);
      document.removeEventListener('pointerup', up);
      document.removeEventListener('pointercancel', up);
      document.removeEventListener('click', click, true);
      document.removeEventListener('contextmenu', menu);
      document.removeEventListener('focusin', focus);
      document.removeEventListener('focusout', close);
      document.removeEventListener('keydown', key);
      window.removeEventListener('scroll', close, true);
    };
  }, []);
  if (!tip) return null;
  // Above the control, or below it when there is no room (the top bar).
  const below = tip.rect.top < 72;
  const centre = tip.rect.left + tip.rect.width / 2;
  const width = typeof window === 'undefined' ? 1024 : window.innerWidth;
  const left = Math.min(Math.max(centre, 120), width - 120);
  return (
    <div
      className={css.tooltip}
      role="tooltip"
      data-testid="tooltip"
      style={{
        left,
        top: below ? tip.rect.bottom + 8 : tip.rect.top - 8,
        transform: below ? 'translate(-50%, 0)' : 'translate(-50%, -100%)',
      }}
    >
      <span className={css.tooltipLabel}>
        {tip.label}
        {tip.keyHint ? <kbd className={css.tooltipKey}>{tip.keyHint}</kbd> : null}
      </span>
      {tip.why ? <span className={css.tooltipWhy}>{tip.why}</span> : null}
    </div>
  );
}
